import { EnvironmentOutlined, ShareAltOutlined } from '@ant-design/icons';
import cn from 'classnames';
import DOMPurify from 'dompurify';
import { useState } from 'react';

import {
  LEAD_MSG_STATUS_DELETED,
  formatLeadMessageText,
  formatLeadMessageTimestamp,
  getLeadMessageStatusColor,
  getLeadMessageStatusIcon,
} from '@app/components/LeadCard/ui/MessageHistory/lib';

import type { FC, SyntheticEvent } from 'react';

import './LeadLocationMessage.scss';

const buildLocationMapUrl = (locationLatLon: string): string => {
  // Backend format: "lat, lon" — 2gis static API expects "lon,lat"
  const parts = locationLatLon.split(', ');
  if (parts.length !== 2) return '';
  const coordinates = `${parts[1]},${parts[0]}`;
  return `https://static.maps.2gis.com/1.0?zoom=14&size=200,100&markers=${coordinates}`;
};

interface LeadLocationMessageProps {
  locationLatLon: string;
  locationName: string | null;
  locationAddress: string | null;
  message: string | null;
  socialType: string | null;
  isIncoming: boolean;
  timestamp: string;
  wasEdited: boolean;
  status: string | null;
  isForwarded: boolean;
  replyMessage: string | null;
  replyMessageId: number | null;
}

export const LeadLocationMessage: FC<LeadLocationMessageProps> = ({
  locationLatLon,
  locationName,
  locationAddress,
  message,
  socialType,
  isIncoming,
  timestamp,
  wasEdited,
  status,
  isForwarded,
  replyMessage,
  replyMessageId,
}) => {
  const [hasMapError, setHasMapError] = useState(false);

  const isDeleted = status === LEAD_MSG_STATUS_DELETED;
  const showStatusIcon = !isIncoming || isDeleted;
  const statusIcon = showStatusIcon ? getLeadMessageStatusIcon(status) : null;
  const statusColor = getLeadMessageStatusColor(status);
  const formattedTimestamp = formatLeadMessageTimestamp(timestamp);

  const mapUrl = buildLocationMapUrl(locationLatLon);

  const sanitizedReplyHtml = replyMessage
    ? DOMPurify.sanitize(formatLeadMessageText(replyMessage, socialType))
    : null;

  const sanitizedMessageHtml = message
    ? DOMPurify.sanitize(formatLeadMessageText(message, socialType))
    : null;

  const handleMapError = (e: SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.style.display = 'none';
    setHasMapError(true);
  };

  const locationLabel = locationName ?? locationAddress ?? locationLatLon;

  return (
    <div
      className={cn('LeadLocationMessage', {
        'LeadLocationMessage--out': !isIncoming,
        'LeadLocationMessage--deleted': isDeleted,
      })}
    >
      {isForwarded && (
        <div className="LeadLocationMessage__forwarded">
          <ShareAltOutlined className="LeadLocationMessage__forwarded-icon" />
          Пересланное сообщение
        </div>
      )}
      {sanitizedReplyHtml && (
        <div
          className="LeadLocationMessage__reply"
          data-reply-target-id={replyMessageId ?? undefined}
        >
          <div className="LeadLocationMessage__reply-indicator" />
          <div
            className="LeadLocationMessage__reply-text"
            dangerouslySetInnerHTML={{ __html: sanitizedReplyHtml }}
          />
        </div>
      )}
      <div className="LeadLocationMessage__map-area">
        {mapUrl && !hasMapError && (
          <img
            src={mapUrl}
            alt="Карта"
            className="LeadLocationMessage__map"
            onError={handleMapError}
          />
        )}
        {(!mapUrl || hasMapError) && (
          <div className="LeadLocationMessage__map-fallback">
            <EnvironmentOutlined className="LeadLocationMessage__map-fallback-icon" />
            <span>{locationLabel}</span>
          </div>
        )}
      </div>
      {locationName && (
        <div className="LeadLocationMessage__location-name">{locationName}</div>
      )}
      {locationAddress && locationAddress !== locationName && (
        <div className="LeadLocationMessage__location-address">
          {locationAddress}
        </div>
      )}
      {sanitizedMessageHtml && (
        <div
          className="LeadLocationMessage__text"
          dangerouslySetInnerHTML={{ __html: sanitizedMessageHtml }}
        />
      )}
      <div className="LeadLocationMessage__status">
        <span className="LeadLocationMessage__timestamp">
          {wasEdited && 'изменено '}
          {formattedTimestamp}
        </span>
        {statusIcon && (
          <span
            className="LeadLocationMessage__status-icon"
            style={{ color: statusColor }}
          >
            {statusIcon}
          </span>
        )}
      </div>
    </div>
  );
};
